"use client";

import React from "react";
import { SectionHeader } from "fansunited-sports-ui";
import { LeaderboardCategory } from "@/components/leaderboards/LeaderboardCategory";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import type { LeadersData } from "../../context/types";

const ROWS_PER_CATEGORY = 5;

export interface LeadersTablesProps {
    /** Leaders data (top scorers, assisters, contributors) - fetched server-side */
    leaders?: LeadersData | null;
}

/**
 * Homepage Leaders section — top scorers, assisters, and contributors.
 * Thin wrapper around `LeaderboardCategory` (capped at 5 rows for the homepage's space budget).
 */
export const LeadersTables: React.FC<LeadersTablesProps> = ({ leaders }) => {
    const labels = useLabels();
    const { allowPlayerPhotos } = useTenantSettings();

    if (!leaders) return null;

    const { topScorers, topAssisters, topContributors } = leaders;
    if (topScorers.length === 0 && topAssisters.length === 0 && topContributors.length === 0) {
        return null;
    }

    const playerLabel = labels.teamPage.stats.player;

    return (
        <section>
            <SectionHeader
                entity={{
                    name: labels.homePage.leaders,
                    styles: {
                        typography: { name: "text-sm uppercase font-bold text-foreground" },
                    },
                }}
                styles={{ container: "mb-4" }}
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <LeaderboardCategory
                    title={labels.homePage.topScorers}
                    entries={topScorers}
                    statHeader={labels.stats.goals}
                    statLabel={labels.stats.goals}
                    playerLabel={playerLabel}
                    allowPlayerPhotos={allowPlayerPhotos}
                    rowCount={ROWS_PER_CATEGORY}
                />
                <LeaderboardCategory
                    title={labels.homePage.topAssisters}
                    entries={topAssisters}
                    statHeader={labels.stats.assists}
                    statLabel={labels.stats.assists}
                    playerLabel={playerLabel}
                    allowPlayerPhotos={allowPlayerPhotos}
                    rowCount={ROWS_PER_CATEGORY}
                />
                <LeaderboardCategory
                    title={labels.homePage.goalContributions}
                    entries={topContributors}
                    statHeader={labels.homePage.contributions}
                    statLabel={labels.homePage.contributions}
                    playerLabel={playerLabel}
                    allowPlayerPhotos={allowPlayerPhotos}
                    rowCount={ROWS_PER_CATEGORY}
                />
            </div>
        </section>
    );
};
